import { last, orderBy } from 'lodash-es'

import { createSyncable, RowBase } from '@/lib/sync-manager/helpers.ts'
import {
  comparePullCheckpoints,
  initialPullCheckpoint,
  pullFromServer,
  type PullCheckpoint,
} from '@/lib/sync-manager/pull-checkpoint'
import { pushToServer } from '@/lib/sync-manager/push-to-server'
import { reportSyncFailure } from '@/lib/sync-manager/report-sync-failure'
import { SyncRequestError } from '@/lib/sync-manager/sync-errors'
import type { DexieDatabase, InterventoDoc } from '@/db/local/dexie-db.ts'
import {
  refusalNotices,
  showRefusalNotices,
} from '@/features/riparazioni/refused-notices'
import { neverSaved } from './save-intervento'

// Define a type that combines the Intervento with RowBase requirements
type InterventoRow = InterventoDoc & RowBase

const pushInterventi = pushToServer<InterventoRow>('interventi')

/**
 * Pushes interventi with their events in their own input, never inside the
 * documents. An API that takes events answers every intervento with its
 * `eventi`; one without them (the UI deployed first, or the API rolled back)
 * would drop the events and answer as if written. Documents whose events went
 * that way stay unsent, and the failure is reported, until the two sides match.
 * Events the server refused come back as its row without them, which replaces
 * the device's copy; the user is told.
 */
export async function pushInterventiWithEvents(rows: InterventoRow[]) {
  const orderedRows = orderBy(rows, ['updated_at'], ['asc'])
  const documents = orderedRows.map(({ eventi: _, ...row }) => row)
  const events = orderedRows.flatMap((row) => row.eventi ?? [])

  const { records, refused } = await pushInterventi(documents, events)
  showRefusalNotices(refusalNotices(orderedRows, refused))
  const checkpoint = last(orderedRows)?.updated_at

  const withEvents = new Set(
    orderedRows.filter((row) => row.eventi?.length).map((row) => row.id)
  )
  const dropped = new Set(
    records
      .filter((record) => withEvents.has(record.id) && !('eventi' in record))
      .map((record) => record.id)
  )
  if (dropped.size === 0) return { checkpoint, records }

  const error = new SyncRequestError(
    'server',
    `The API did not take the events of ${dropped.size} interventi`
  )
  reportSyncFailure('trpc-interventi-push-eventi', error)
  return {
    checkpoint,
    records: records.filter((record) => !dropped.has(record.id)),
    sent: orderedRows.filter((row) => !dropped.has(row.id)),
    error,
  }
}

export const createInterventiSyncable = (db: DexieDatabase) =>
  createSyncable<InterventoRow, PullCheckpoint | Date>({
    db,
    name: 'interventi',
    push: {
      batchSize: 200,
      handler: pushInterventiWithEvents,
      // A draft never saved can't be stored by the server (no tipologia), and
      // one such row would fail the whole push every time: it waits for its
      // first save
      holdBack: (rows) => rows.filter(neverSaved),
    },

    pull: {
      batchSize: 1200,
      initialCheckpoint: initialPullCheckpoint,
      compareCheckpoints: comparePullCheckpoints,
      handler: pullFromServer<InterventoRow>('interventi'),
    },
  })
